import {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import * as XLSX from 'xlsx';
import {getMeetingSatisfactionResponses} from '../../API/apiService';
import './style.scss';

const QUESTIONS = [
  ['teacher_satisfaction', 'Satisfacción con el docente'],
  ['administrative_satisfaction', 'Atención del personal administrativo'],
  ['recommendation_score', 'Probabilidad de recomendar la sesión'],
  ['module_relevance_score', 'Pertinencia del módulo'],
  ['session_takeaway', 'Palabra o idea de la sesión'],
  ['program_comments', 'Comentario o recomendación']
];

const SCALE_QUESTIONS = [
  {key: 'teacher_satisfaction', number: 1, label: 'Satisfacción con el docente', maximum: 5},
  {key: 'administrative_satisfaction', number: 2, label: 'Atención del personal administrativo', maximum: 5},
  {key: 'recommendation_score', number: 3, label: 'Probabilidad de recomendar la sesión', maximum: 10},
  {key: 'module_relevance_score', number: 4, label: 'Pertinencia del módulo', maximum: 10}
];

function buildQuestionSummary(responses, question) {
  const values = responses.map(response => Number(response[question.key]))
    .filter(value => Number.isInteger(value) && value >= 1 && value <= question.maximum);
  const counts = Array.from({length: question.maximum}, (_, index) => ({
    score: index + 1,
    count: values.filter(value => value === index + 1).length
  }));
  const average = values.length
    ? values.reduce((total, value) => total + value, 0) / values.length
    : 0;
  return {...question, average, counts, total: values.length};
}

function safeFilePart(value, fallback) {
  const cleaned = String(value || '').trim().replace(/[^a-zA-Z0-9_-]+/g, '_');
  return cleaned || fallback;
}

export default function SatisfactionResponses() {
  const [searchParams] = useSearchParams();
  const meetingId = searchParams.get('encuentro');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('summary');

  useEffect(() => {
    getMeetingSatisfactionResponses(meetingId).then(response => {
      setData(response);
      setLoading(false);
    });
  }, [meetingId]);

  const responses = useMemo(
    () => Array.isArray(data?.responses) ? data.responses : [],
    [data?.responses]
  );
  const summaries = useMemo(
    () => SCALE_QUESTIONS.map(question => buildQuestionSummary(responses, question)),
    [responses]
  );

  function downloadIndividualResponses() {
    const rows = responses.map((response, index) => ({
      '#': index + 1,
      'Nombre completo': response.full_name || '',
      Documento: response.document || '',
      'Fecha de respuesta': response.created_at
        ? new Date(response.created_at).toLocaleString('es-CO')
        : '',
      'Satisfacción con el docente (1 a 5)': response.teacher_satisfaction,
      'Atención del personal administrativo (1 a 5)': response.administrative_satisfaction,
      'Probabilidad de recomendar la sesión (1 a 10)': response.recommendation_score,
      'Pertinencia del módulo (1 a 10)': response.module_relevance_score,
      'Palabra o idea de la sesión': response.session_takeaway || '',
      'Comentario o recomendación': response.program_comments || ''
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), 'Respuestas individuales');
    XLSX.writeFile(workbook,
      `satisfaccion_individual_${safeFilePart(data?.meeting?.code, meetingId || 'encuentro')}.xlsx`);
  }

  function downloadSummary() {
    const summaryRows = summaries.map(summary => {
      const row = {
        Pregunta: summary.label,
        Escala: `1 a ${summary.maximum}`,
        'Total de respuestas': summary.total,
        Promedio: Number(summary.average.toFixed(2))
      };
      summary.counts.forEach(item => {
        row[`Puntaje ${item.score}`] = item.count;
        row[`Puntaje ${item.score} (%)`] = summary.total
          ? Number(((item.count / summary.total) * 100).toFixed(2))
          : 0;
      });
      return row;
    });
    const openAnswerRows = responses.map((response, index) => ({
      '#': index + 1,
      'Nombre completo': response.full_name || '',
      Documento: response.document || '',
      'Palabra o idea de la sesión': response.session_takeaway || '',
      'Comentario o recomendación': response.program_comments || ''
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryRows), 'Conglomerado');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(openAnswerRows), 'Respuestas abiertas');
    XLSX.writeFile(workbook,
      `satisfaccion_conglomerado_${safeFilePart(data?.meeting?.code, meetingId || 'encuentro')}.xlsx`);
  }

  return <main className='satisfaction-responses'>
    <header>
      <h1>Respuestas del formulario de satisfacción</h1>
      <p>{data?.meeting?.code || ''}{data?.meeting?.order ? ` · Encuentro ${data.meeting.order}` : ''}</p>
    </header>
    {!loading && responses.length > 0 && <nav className='satisfaction-responses__tabs'>
      <button type='button' className={activeView === 'summary' ? 'active' : ''}
        onClick={() => setActiveView('summary')}>Conglomerado</button>
      <button type='button' className={activeView === 'individual' ? 'active' : ''}
        onClick={() => setActiveView('individual')}>Una a una</button>
    </nav>}
    {!loading && responses.length > 0 && <div className='satisfaction-responses__downloads'>
      <button type='button' onClick={downloadSummary}>
        <span className='material-symbols-rounded'>download</span>
        Descargar conglomerado
      </button>
      <button type='button' onClick={downloadIndividualResponses}>
        <span className='material-symbols-rounded'>download</span>
        Descargar respuestas individuales
      </button>
    </div>}
    {loading ? <p>Cargando respuestas...</p> : responses.length === 0
      ? <section className='satisfaction-responses__empty'>Aún no hay respuestas para este encuentro.</section>
      : activeView === 'summary' ? <section className='satisfaction-responses__summary'>
        <div className='satisfaction-responses__total'>
          <span>{responses.length}</span>
          <p>{responses.length === 1 ? 'Respuesta recibida' : 'Respuestas recibidas'}</p>
        </div>
        <div className='satisfaction-responses__metrics'>
          {summaries.map(summary => <article key={summary.key}>
            <div className='satisfaction-responses__metric-heading'>
              <div><small>Pregunta {summary.number}</small><h2>{summary.label}</h2></div>
              <strong>{summary.average.toFixed(1)}<span>/{summary.maximum}</span></strong>
            </div>
            <div className='satisfaction-responses__distribution'>
              {summary.counts.map(item => {
                const percentage = summary.total ? (item.count / summary.total) * 100 : 0;
                return <div key={item.score}>
                  <span>{item.score}</span><div><i style={{width: `${percentage}%`}} /></div>
                  <small>{item.count} ({Math.round(percentage)}%)</small>
                </div>;
              })}
            </div>
          </article>)}
        </div>
        <div className='satisfaction-responses__open-answers'>
          <article><h2>Palabras o ideas de la sesión</h2>
            <ul>{responses.map(response => <li key={`takeaway-${response.id}`}>{response.session_takeaway}</li>)}</ul>
          </article>
          <article><h2>Comentarios y recomendaciones</h2>
            <ul>{responses.map(response => <li key={`comment-${response.id}`}>{response.program_comments}</li>)}</ul>
          </article>
        </div>
      </section> : <div className='satisfaction-responses__list'>
          {responses.map((response, index) => <article key={response.id}>
            <div className='satisfaction-responses__person'>
              <span>{index + 1}</span>
              <div><strong>{response.full_name}</strong><small>Documento: {response.document}</small></div>
              <time>{response.created_at ? new Date(response.created_at).toLocaleString('es-CO') : ''}</time>
            </div>
            <dl>{QUESTIONS.map(([key, label]) => <div key={key}>
              <dt>{label}</dt><dd>{response[key]}</dd>
            </div>)}</dl>
          </article>)}
        </div>}
  </main>;
}
